import React, {
    ForwardedRef,
    ReactElement,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { Platform, TextInput as RNTextInput, PixelRatio, useWindowDimensions } from "react-native";
import {
    RecyclerListView,
    DataProvider,
    RecyclerListViewProps,
    LayoutProvider,
} from "recyclerlistview";
import { useDebouncedCallback } from "use-debounce";
import { ShadowedView, shadowStyle } from "react-native-fast-shadow";
import { useTheme } from "../../hooks/use-theme";
import { StyleSheet, View, KeyboardTypeOptions } from "react-native";
import VStack from "../../ui_atoms/VStack";
import ListItem, { ListItemProps } from "../../ui_atoms/ListItem";
import { processSelectItems, sleep } from "../../utils";
import Callout from "../../ui_atoms/Callout";
import Modal, { ModalSize } from "../Modal";
import TextField from "../TextField";
import IconButton from "../IconButton";
import Loading from "../Loading";
import Text from "../Text";
import { BodyMediumText } from "../FormattedText";
import Animated, {
    useAnimatedStyle,
    useSharedValue,
    withTiming,
    Easing,
} from "react-native-reanimated";
import Button from "../Button";
import { useI18n } from "../../hooks/i18n-hooks";
import { RecyclerListViewState } from "recyclerlistview/dist/reactnative/core/RecyclerListView";
import OutsidePressHandler from "react-native-outside-press";
import { setStatusBarStyle } from "expo-status-bar";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const FONT_SCALE = PixelRatio.getFontScale();
const ITEM_MARGIN_SIZE = 16;
const ITEM_LINE_SIZE = 33;
const MIN_ITEM_HEIGHT = ITEM_MARGIN_SIZE + ITEM_LINE_SIZE * (FONT_SCALE >= 1 ? FONT_SCALE : 1);

const BACKGROUND_RGB = "26, 21, 54";
const ANIMATION_DELAY = 300;
const MODAL_DELAY = ANIMATION_DELAY;
const ANIMATION_CONFIG = {
    duration: ANIMATION_DELAY,
    easing: Easing.out(Easing.poly(4)),
};

const BACKGROUND_ANIMATION_CONFIG = {
    duration: MODAL_DELAY,
};

const MIN_HALF_SCREEN_HEIGHT = 80;

export type SelectItemType<V> = {
    value: V;
    label: string;
} & ListItemProps;

export type SearchItemsFunction<V> = (
    filter: string,
) => Promise<SelectItemType<V>[] | null> | SelectItemType<V>[] | null;

export const isSelectItemType = <V,>(value: unknown): value is SelectItemType<V> =>
    !!(value as SelectItemType<V>).value;

export type SelectChooserProps<V> = {
    onSelect?: (selectedItem: SelectItemType<V>) => void | Promise<void>;
    searchItems: SearchItemsFunction<V> | SelectItemType<V>[] | Promise<SelectItemType<V>[]> | null;
    onDismiss: () => void;
    visible: boolean;
    placeholder?: string;
    emptyText?: string;
    emptyResult?: JSX.Element;
    resultTitle?: string;
    tipText?: string;
    selectedValue?: V;
    searchKeyboardType?: KeyboardTypeOptions;
    halfScreen?: boolean;
    staticTitle?: string;
    isDynamicHeight?: boolean;
    selectable?: boolean;
    topComponent?: React.ReactNode;
    bottomComponent?: React.ReactNode;
    lightMode?: boolean;
    disabled?: boolean;
    isLegacyLabel?: boolean;
    useDebounce?: boolean;
};

/**
 * This component is used to add a transparent footer on the bottom off the select list
 * to be able to scroll the last item on top of the "home" bar on ios
 *
 * @constructor
 */
const SelectFooter = () => {
    const insets = useSafeAreaInsets();

    return <View style={{ height: insets.bottom }} />;
};

const AnimatedVStack = Animated.createAnimatedComponent(VStack);

export type SelectChooserRef = {
    forceRerender: () => void;
};

// eslint-disable-next-line react/display-name
export const SelectChooser = forwardRef(
    <V,>(
        {
            onSelect,
            searchItems,
            onDismiss,
            visible,
            placeholder,
            emptyText,
            emptyResult,
            resultTitle,
            tipText,
            selectedValue,
            searchKeyboardType,
            halfScreen = false,
            staticTitle,
            isDynamicHeight = halfScreen,
            selectable = true,
            topComponent,
            bottomComponent,
            lightMode = false,
            disabled = false,
            isLegacyLabel = true,
            useDebounce = false,
        }: SelectChooserProps<V>, // Generic type V is used correctly here
        ref: ForwardedRef<SelectChooserRef>,
    ): ReactElement | null => {
        const { colors, marginSize } = useTheme();
        const i18n = useI18n();
        const [searchQuery, setSearchQuery] = React.useState("");
        const [isSearching, setIsSearching] = React.useState(false);
        const [showEmptytext, setShowEmptytext] = React.useState(true);
        const [items, setItems] = React.useState<SelectItemType<V>[]>([]);
        const [staticItems, setStaticItems] = useState(Array.isArray(searchItems));
        const textInput = React.useRef<RNTextInput | null>(null);
        const [width, setWidth] = useState<number>();
        const { height: windowHeight } = useWindowDimensions();

        const styles = useStyles();

        const maxSelectorHeight = useMemo(() => windowHeight * 0.8, [windowHeight]);
        const debouncedProcessSelectItems = useDebouncedCallback(processSelectItems<V>, 1000);
        React.useEffect(() => {
            let canceled = false;

            if (Array.isArray(searchItems)) {
                setItems(searchItems);
            } else {
                setIsSearching(true);
                if (useDebounce) {
                    debouncedProcessSelectItems(
                        searchItems,
                        searchQuery,
                        (newItems: SelectItemType<V>[]) => {
                            if (!canceled) {
                                setItems(newItems);
                                setIsSearching(false);
                            }
                        },
                    );
                } else {
                    const { isStaticItems } = processSelectItems(
                        searchItems,
                        searchQuery,
                        (newItems) => {
                            if (!canceled) {
                                setItems(newItems);
                                setIsSearching(false);
                            }
                        },
                    );

                    setStaticItems(isStaticItems);
                }
            }

            return () => {
                canceled = true;
            };
        }, [searchQuery, debouncedProcessSelectItems, searchItems, useDebounce]);

        React.useEffect(() => {
            setShowEmptytext(searchQuery.length < 3);
        }, [searchQuery]);

        const [isHalfScreenOpen, setIsHalfScreenOpen] = useState(false);
        const [isRendered, setIsRendered] = useState(false);

        const onClose = useCallback(async () => {
            void setSearchQuery("");
            if (halfScreen) {
                setIsHalfScreenOpen(false);
                await sleep(MODAL_DELAY);
            }
            setIsRendered(false);
            void onDismiss();
        }, [halfScreen, onDismiss]);

        const recyclerRef =
            useRef<RecyclerListView<RecyclerListViewProps, RecyclerListViewState>>(null);

        const forceRerender = useCallback(() => {
            recyclerRef.current?.forceRerender();
        }, []);

        useImperativeHandle(ref, () => ({
            forceRerender,
        }));

        useEffect(() => {
            // automatically re-render items if one of them have changed
            // and the SelectChooser is already visible
            if (visible) {
                forceRerender();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [forceRerender, items]);

        const rowRenderer = useCallback<RecyclerListViewProps["rowRenderer"]>(
            (_type, item: SelectItemType<V>) => {
                const { label, value, ...itemProps } = item;
                const checked = label === selectedValue || value === selectedValue;
                return (
                    <ListItem
                        selectable={selectable}
                        style={{ width }}
                        {...(isLegacyLabel
                            ? {
                                  title: label,
                              }
                            : { title: itemProps.title, labels: [{ text: label }] })}
                        onPress={async () => {
                            void onSelect?.(item);
                            if (halfScreen) {
                                if (itemProps.loading === undefined) {
                                    setIsHalfScreenOpen(false);
                                }
                                forceRerender();
                                await sleep(MODAL_DELAY);
                                setIsRendered(false);
                            }
                        }}
                        {...itemProps}
                        {...(checked
                            ? {
                                  checked: lightMode ? "light" : true,
                              }
                            : {})}
                    />
                );
            },
            [
                selectedValue,
                selectable,
                width,
                isLegacyLabel,
                lightMode,
                onSelect,
                halfScreen,
                forceRerender,
            ],
        );

        const dataProvider = useMemo(
            () =>
                new DataProvider((r1: SelectItemType<V>, r2: SelectItemType<V>) => {
                    return r1.value !== r2.value;
                }),
            [],
        );

        const layoutProvider = useMemo(
            () =>
                new LayoutProvider(
                    () => 0,
                    (_type, dim) => {
                        if (width) {
                            dim.width = width;
                        }
                        if (!halfScreen) {
                            dim.height = MIN_HALF_SCREEN_HEIGHT;
                        } else if (!isDynamicHeight || items.length === 1) {
                            dim.height = MIN_ITEM_HEIGHT;
                        }
                    },
                ),
            [width, halfScreen, isDynamicHeight, items.length],
        );

        const data = useMemo(() => dataProvider.cloneWithRows(items), [dataProvider, items]);

        React.useEffect(() => {
            if (visible && halfScreen) {
                setIsHalfScreenOpen(true);
                setIsRendered(true);
            }
        }, [halfScreen, visible]);

        const [itemsHeight, setItemsHeight] = useState(halfScreen ? 1 : undefined);
        const [topHeight, setTopHeight] = useState(0);
        const [closeHeight, setCloseHeight] = useState(0);
        const [isScrollable, setIsScrollable] = useState(false);

        const translateY = useSharedValue(0);
        const backgroundColor = useSharedValue(`rgba(${BACKGROUND_RGB}, 0)`);

        const animatedStyles = useAnimatedStyle(() => ({
            transform: [{ translateY: translateY.value }],
        }));

        const backgroundAnimatedStyles = useAnimatedStyle(() => ({
            backgroundColor: backgroundColor.value,
        }));

        const setStatusBarLight = useCallback(() => {
            setStatusBarStyle("light");
        }, []);

        const setStatusBarDark = useCallback(() => {
            setStatusBarStyle("dark");
        }, []);

        React.useEffect(() => {
            if (halfScreen) {
                if (visible) {
                    setStatusBarLight();
                } else {
                    setStatusBarDark();
                }
            }
        }, [visible, setStatusBarDark, halfScreen, setStatusBarLight]);

        const onVisibleIndicesChanged = useCallback(() => {
            if (halfScreen) {
                const contentHeight =
                    layoutProvider.getLayoutManager()?.getContentDimension().height ||
                    MIN_HALF_SCREEN_HEIGHT;
                if (!contentHeight) return;

                if (contentHeight > maxSelectorHeight) {
                    setIsScrollable(true);
                }

                const finalItemsHeight =
                    contentHeight > maxSelectorHeight ? maxSelectorHeight : contentHeight;

                // On first click of the element (itemsHeight === 1)
                if (itemsHeight === 1) {
                    translateY.value = withTiming(finalItemsHeight + topHeight + closeHeight, {
                        duration: 0,
                    });
                }

                setItemsHeight(finalItemsHeight);

                translateY.value = withTiming(0, ANIMATION_CONFIG);
                backgroundColor.value = withTiming(
                    `rgba(${BACKGROUND_RGB}, 0.6)`,
                    BACKGROUND_ANIMATION_CONFIG,
                );
            }
        }, [
            halfScreen,
            layoutProvider,
            maxSelectorHeight,
            itemsHeight,
            translateY,
            backgroundColor,
            topHeight,
            closeHeight,
        ]);

        React.useEffect(() => {
            if (halfScreen && !isHalfScreenOpen) {
                translateY.value = withTiming(
                    (itemsHeight || 0) + closeHeight + topHeight,
                    ANIMATION_CONFIG,
                );
                backgroundColor.value = withTiming(
                    `rgba(${BACKGROUND_RGB}, 0)`,
                    BACKGROUND_ANIMATION_CONFIG,
                );
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [isHalfScreenOpen]);

        const onItemLayout = useCallback<NonNullable<RecyclerListViewProps["onItemLayout"]>>(
            (itemIndex) => {
                if (halfScreen && itemIndex === items.length - 1) {
                    onVisibleIndicesChanged();
                }
            },
            [halfScreen, items.length, onVisibleIndicesChanged],
        );

        const onOutsidePress = useCallback(() => {
            void onClose();
        }, [onClose]);

        return (
            <Modal
                visible={visible || isRendered}
                animationType={halfScreen ? "none" : "slide"}
                {...(halfScreen
                    ? {
                          transparent: true,
                      }
                    : {})}
                statusBarTranslucent
                hideContainerBackground
                hardwareAccelerated
                // workaround to fix React native bug @see https://github.com/react-native-modal/react-native-modal/issues/516
                onShow={() => setTimeout(() => textInput.current?.focus(), 100)}
                size={ModalSize.full}
                onClose={() => void onClose()}
                hideCloseButton
                edges={{
                    top: "additive",
                    left: "additive",
                    bottom: "off",
                    right: "additive",
                }}
            >
                {halfScreen ? (
                    <Animated.View
                        style={[
                            {
                                height: "100%",
                                width: "100%",
                            },
                            backgroundAnimatedStyles,
                        ]}
                    />
                ) : null}
                <AnimatedVStack
                    gap={!halfScreen ? 1 : 0}
                    top={!halfScreen ? 0.5 : 0}
                    style={[
                        styles.stackView,
                        halfScreen && styles.halfStackView,
                        halfScreen && animatedStyles,
                        itemsHeight === 1 && { opacity: 0 },
                    ]}
                    onLayout={(e) => {
                        setWidth(e.nativeEvent.layout.width);
                    }}
                >
                    {!halfScreen ? (
                        <View style={styles.topView}>
                            {!staticItems ? (
                                <TextField
                                    ref={textInput}
                                    autoFocus={Platform.OS !== "android"}
                                    style={{
                                        flexShrink: 1,
                                        backgroundColor: colors.background,
                                    }}
                                    label={placeholder}
                                    onChangeText={setSearchQuery}
                                    value={searchQuery}
                                    keyboardType={searchKeyboardType}
                                />
                            ) : staticTitle ? (
                                <Text style={styles.staticTitle} numberOfLines={1}>
                                    {staticTitle}
                                </Text>
                            ) : resultTitle ? (
                                <Text style={styles.staticTitle} numberOfLines={1}>
                                    {resultTitle}
                                </Text>
                            ) : (
                                <View />
                            )}
                            <IconButton
                                icon="close"
                                size={24}
                                style={styles.staticHeaderIcon}
                                onPress={() => void onClose()}
                            />
                        </View>
                    ) : null}
                    {isSearching || !width ? (
                        <Loading />
                    ) : items.length > 0 ? (
                        <OutsidePressHandler
                            disabled={!halfScreen || disabled}
                            style={{ flex: 1 }}
                            onOutsidePress={onOutsidePress}
                        >
                            {topComponent && (
                                <View
                                    style={styles.topComponentStyleContainer}
                                    onLayout={(e) => {
                                        setTopHeight(e.nativeEvent.layout.height);
                                    }}
                                >
                                    {topComponent}
                                </View>
                            )}
                            {tipText && (
                                <View
                                    style={{
                                        alignSelf: "stretch",
                                        paddingHorizontal: marginSize,
                                        paddingBottom: marginSize,
                                    }}
                                >
                                    <BodyMediumText color={colors.secondary}>
                                        {tipText}
                                    </BodyMediumText>
                                </View>
                            )}
                            <RecyclerListView
                                ref={recyclerRef}
                                forceNonDeterministicRendering={isDynamicHeight}
                                style={{
                                    height: itemsHeight,
                                    alignSelf: "stretch",
                                    width,
                                }}
                                scrollViewProps={{
                                    keyboardShouldPersistTaps: "always",
                                }}
                                layoutProvider={layoutProvider}
                                dataProvider={data}
                                rowRenderer={rowRenderer}
                                onItemLayout={onItemLayout}
                                renderFooter={() => (halfScreen ? null : <SelectFooter />)}
                            />
                        </OutsidePressHandler>
                    ) : (
                        <View style={{ alignSelf: "stretch", marginHorizontal: marginSize }}>
                            {showEmptytext && emptyText ? (
                                <Callout markdown={emptyText} />
                            ) : emptyResult ? (
                                emptyResult
                            ) : null}
                        </View>
                    )}
                    {halfScreen && width ? (
                        <ShadowedView
                            style={shadowStyle({
                                opacity: isScrollable ? 0.175 : 0,
                                radius: 8.8,
                                offset: [0, -3],
                            })}
                        >
                            <VStack
                                onLayout={(e) => {
                                    setCloseHeight(e.nativeEvent.layout.height);
                                }}
                            >
                                {bottomComponent}
                                <VStack h="2/3" v="2/3" style={styles.halfStackButton}>
                                    <Button
                                        label={i18n.t("close")}
                                        buttonMode="outlined"
                                        onPress={onClose}
                                        disabled={disabled}
                                        adjustsTextFontSizeToFit={false}
                                    />
                                </VStack>
                            </VStack>
                        </ShadowedView>
                    ) : null}
                </AnimatedVStack>
            </Modal>
        );
    },
) as <V>(
    props: SelectChooserProps<V> & React.RefAttributes<SelectChooserRef>,
) => ReactElement | null;

const useStyles = () => {
    const { gridSize, colors, fonts, marginSize, roundness } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                stackView: {
                    flex: 1,
                    flexDirection: "column",
                    backgroundColor: colors.background,
                },
                halfStackView: {
                    position: "absolute",
                    width: "100%",
                    borderTopLeftRadius: roundness * 2,
                    borderTopRightRadius: roundness * 2,
                    bottom: 0,
                    overflow: "hidden",
                },
                halfStackButton: {
                    padding: gridSize / 2,
                    backgroundColor: colors.background,
                    alignSelf: "stretch",
                },
                topView: {
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginHorizontal: marginSize,
                },
                staticHeaderIcon: {
                    margin: 0,
                    marginLeft: gridSize / 2,
                },
                staticTitle: {
                    alignSelf: "stretch",
                    backgroundColor: colors.background,
                    ...fonts.headlineMedium,
                    marginVertical: gridSize / 2,
                },
                topComponentStyleContainer: {
                    borderBottomWidth: 1,
                    borderBottomColor: colors.border,
                },
            }),
        [colors.background, colors.border, fonts.headlineMedium, gridSize, marginSize, roundness],
    );
};
